import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiFileText, FiDollarSign, FiArrowRight, FiAlertCircle } from "react-icons/fi";
import { fetchContractById } from "../../api"; // Adjust to your api path
import { IoMdArrowBack } from "react-icons/io";

const Checkcontract = () => {
  const navigate = useNavigate();

  // Input state for contract ID search
  const [contractId, setContractId] = useState("");
  const [searched, setSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Contract details fetched from backend
  const [contractDetails, setContractDetails] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!contractId.trim()) return;

    setIsLoading(true);
    setSearched(false);
    setErrorMessage("");
    setContractDetails(null);

    try {
      const response = await fetchContractById(contractId.trim());

      if (response.success && response.data) {
        setContractDetails(response.data);
        setSearched(true);
      } else {
        setSearched(true);
      }
    } catch (error) {
      setSearched(true);
      setErrorMessage(error.response?.data?.message || "Failed to fetch contract.");
    } finally {
      setIsLoading(false);
    }
  };

const handleProceed = () => {
  if (!contractDetails) return;

  const enteredId = contractId.trim();

  if (contractDetails.creatorLinkId === enteredId) {
    navigate(`/viewcontract/${enteredId}`);
    return;
  }

  if (contractDetails.counterpartyLinkId === enteredId) {
    navigate(`/othercontract/${enteredId}`);
    return;
  }

  // If they entered MongoDB _id
  navigate(`/viewcontract/${enteredId}`);
};

  return (
    <div className="bg-gray-50 min-h-screen pb-12 relative">
      {/* 🔝 Sticky Header */}
     <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-center relative">
  <button
    onClick={() => navigate(-1)}
    className="absolute left-4 p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
  >
    <IoMdArrowBack size={24} />
    <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
  </button>

  <h1 className="text-lg font-black text-gray-900 tracking-tight">
    Lookup Contract
  </h1>
</div>

      <div className="max-w-xl mx-auto px-6 pt-8 space-y-8">
        
        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
          <div className="space-y-2">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <FiSearch className="text-orange-600" /> Enter Contract ID
            </h3>
            <p className="text-xs font-bold text-gray-500 leading-relaxed">
              Type or paste your unique contract reference ID or link ID below to quickly pull up agreement terms and details.
            </p>
          </div>

          <form onSubmit={handleSearch} className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Contract ID / Link ID</label>
              <div className="flex gap-3">
                <input
                  type="text"
                  required
                  value={contractId}
                  onChange={(e) => setContractId(e.target.value)}
                  placeholder="e.g., enter contract ID or link ID"
                  className="flex-1 bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-xs text-gray-900 transition-all outline-none"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 bg-orange-600 hover:bg-orange-700 text-white rounded-2xl font-black uppercase tracking-wider text-xs transition shadow-lg shadow-orange-200 active:scale-95 disabled:opacity-50"
                >
                  {isLoading ? "Searching..." : "Fetch"}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Error Alert Display */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-xs font-bold p-4 rounded-2xl text-center">
            {errorMessage}
          </div>
        )}

        {/* 📄 Fetched Contract Details Result */}
        {searched && contractDetails && (
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <div>
                <span className="text-[10px] font-black text-orange-600 uppercase tracking-widest block">Found Contract</span>
                <h4 className="text-base font-black text-gray-900 truncate max-w-[200px]">{contractDetails._id}</h4>
              </div>
              <span className="px-3 py-1 bg-teal-50 text-teal-800 text-[10px] font-black uppercase tracking-widest rounded-xl">
                {contractDetails.status}
              </span>
            </div>

            <div className="space-y-4">
              {/* Item */}
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-xl shadow-sm"><FiFileText className="text-orange-600" size={18} /></div>
                  <div>
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Item Name</span>
                    <p className="font-extrabold text-gray-900 text-xs">{contractDetails.itemTitle}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Amount</span>
                  <p className="font-black text-orange-600 text-xs flex items-center justify-end">
                    <FiDollarSign size={14} />{Number(contractDetails.price).toLocaleString()} ₦
                  </p>
                </div>
              </div>

              {/* Parties */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100">
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-0.5">My Name</span>
                  <p className="font-extrabold text-gray-900 text-xs">{contractDetails.myName}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100">
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-0.5">Other Party</span>
                  <p className="font-extrabold text-gray-900 text-xs">{contractDetails.otherPartyName}</p>
                </div>
              </div>
            </div>

            {/* Action Button to Proceed / View Full Details */}
            <div className="pt-2">
              <button
                type="button"
                onClick={handleProceed}
                className="w-full py-5 bg-gray-900 hover:bg-black text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-xs transition-all shadow-xl shadow-gray-300 active:scale-95 flex items-center justify-center gap-3"
              >
                Proceed to Full Contract
                <FiArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {searched && !contractDetails && !errorMessage && (
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 text-center space-y-3">
            <FiAlertCircle className="mx-auto text-red-500" size={32} />
            <h4 className="text-base font-black text-gray-900">Contract Not Found</h4>
            <p className="text-xs font-bold text-gray-500">Please check your ID and try entering it again.</p>
          </div>
        )}

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Contracts • Instant Verification • OurASAP
        </p>
      </div>
    </div>
  );
};

export default Checkcontract;